import { ArticleSearchResultResponse, Collection, UserAccountType } from "@authory-packages/core";
import * as Yup from "yup";
import { useFormik } from "formik";
import React, { useContext, useEffect, useState } from "react";
import { V3SubtitleMedium } from "../Typography";
import { BulkActionRightPanel, BulkActionsPanel, ContentListItemWrapper, ContentListLoaderOffset } from "./ContentList.styles";
import { V3CheckBox } from "../OptionInput";
import { handleFormikArray } from "../../utils/formikArrayHandling";
import { V3TertiaryButton } from "../Button";
import { V3IconClose } from "../Icons";
import { V3TertiaryCTAColor } from "../Button/V3TertiaryCTA.types";
import { AddToCollectionCTA } from "../AddToCollectionCTA";
import { VerticalSpacing } from "../VerticalSpacing";
import { V3BusinessParentEmpty, V3NoSearchResults } from "../V3EmptyStateScreen";
import { V3NoContent } from "../V3EmptyStateScreen/V3NoContent";
import { V3Loader } from "../Loader";
import { V3ConfirmationModal } from "../V3ConfirmationModal";
import { V3IconBin } from "../Icons";
import { FadeInContainer } from "../ReusableStyledComponents/FadeIn";
import { CreateCollectionHandler } from "../CreateUpdateCollectionModal";
import { InfinitePaginationFooter } from "../InfinitePaginationFooter";
import { BottomRightSideRefPanel } from "../../layout/SideBarLayout";
import ReactDOM from "react-dom";
import { APPLICATION_ROUTES } from "../../shared-logic/routes";
import { ItemList } from "../ItemList";
import { V3ModalIconSegmentVariant } from "../ReusableStyledComponents/ModalIconSegment";
import { UniversalAddContent } from "../UniversalAddContent";
import { OptionalNextLink } from "../ReusableStyledComponents/OptionalNextLink";

interface SelectedContentFormik {
    selectedItems: string[];
}

interface ContentListProps {
    items: ArticleSearchResultResponse[];
    articlesLoading: boolean;
    listReset?: string;
    collections: Collection[];
    hasNoResults: boolean;
    onClearResultsHandler: () => void;
    onDeleteHandler?: (items: string[]) => void;
    addToCollectionHandler?: (items: string[], slug: string) => void;
    isActiveSearch: boolean;
    onCreateCollectionHandler?: CreateCollectionHandler;
    contentLoadmore: () => void;
    contentCanFetchmore: boolean;
    userType?: UserAccountType;
    isSelectOnly?: boolean;
    excludeMode?: boolean;
    onContentSelectedChange?: (items: string[]) => void;
    onFullContentSelected?: () => void;
    onFullContentDeselected?: () => void;
    skipFooterControl?: boolean;
    isInfoBadgeOnly?: boolean;
    scrollRoot?: Element | null;
}

export const ContentList = ({
    items,
    articlesLoading,
    collections,
    listReset = "",
    hasNoResults,
    onClearResultsHandler,
    onDeleteHandler,
    isActiveSearch,
    addToCollectionHandler,
    onCreateCollectionHandler,
    contentLoadmore,
    contentCanFetchmore,
    userType,
    isSelectOnly = false,
    excludeMode = false,
    onContentSelectedChange,
    onFullContentSelected,
    onFullContentDeselected,
    skipFooterControl = false,
    isInfoBadgeOnly = false,
    scrollRoot
}: ContentListProps) => {
    const isBusinessParent = userType === UserAccountType.BusinessParent;

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    const itemsFormik = useFormik<SelectedContentFormik>({
        enableReinitialize: true,
        initialValues: {
            selectedItems: []
        },
        onSubmit: () => {},
        validationSchema: Yup.object().shape({
            selectedItems: Yup.array().of(Yup.string())
        })
    });

    useEffect(() => {
        if (itemsFormik) itemsFormik.resetForm();
    }, [listReset]);

    useEffect(() => {
        const selectedItemsLength = itemsFormik.values.selectedItems.length;

        const sel = document.querySelector<HTMLDivElement>("#intercom-container");
        if (!!selectedItemsLength) {
            if (sel) sel.style.display = "none";
        } else {
            if (sel) sel.removeAttribute("style");
        }

        // optional - notigy the selection changed
        onContentSelectedChange && onContentSelectedChange(itemsFormik.values.selectedItems);

        return () => {
            const sel = document.querySelector("#intercom-container");
            if (sel) sel.removeAttribute("style");
        };
    }, [itemsFormik.values.selectedItems.length]);

    const handleItemsFormikArray = handleFormikArray<SelectedContentFormik>(itemsFormik, excludeMode);

    const IDLE_STATE = hasNoResults && !articlesLoading;

    const BulkActionPanelContainer = () => {
        const bottomRightSideRefPanel = useContext(BottomRightSideRefPanel);

        if (bottomRightSideRefPanel && bottomRightSideRefPanel.current) {
            return ReactDOM.createPortal(
                <>
                    {!!itemsFormik.values.selectedItems.length && (
                        <BulkActionsPanel>
                            <V3SubtitleMedium>
                                {itemsFormik.values.selectedItems.length} {itemsFormik.values.selectedItems.length > 1 ? "items" : "item"} selected
                            </V3SubtitleMedium>
                            <BulkActionRightPanel>
                                <div>
                                    <AddToCollectionCTA
                                        addBtnColor={V3TertiaryCTAColor.white}
                                        collections={collections}
                                        modalDescription="Create new collection with the items you selected."
                                        onCollectionClick={(slug: string) => {
                                            addToCollectionHandler && addToCollectionHandler(itemsFormik.values.selectedItems, slug);
                                            itemsFormik.setFieldValue("selectedItems", []);
                                        }}
                                        customOffset={30}
                                        onCreateCollectionHandler={async (data) => {
                                            if (!onCreateCollectionHandler) return;

                                            const col = await onCreateCollectionHandler(data);

                                            if (!col) return;

                                            addToCollectionHandler && addToCollectionHandler(itemsFormik.values.selectedItems, col.canonicalSlug);

                                            itemsFormik.setFieldValue("selectedItems", []);

                                            return col;
                                        }}
                                    />
                                </div>
                                <div>
                                    <V3TertiaryButton
                                        text="Delete"
                                        icon={<V3IconBin />}
                                        ctaColor={V3TertiaryCTAColor.white}
                                        onClick={() => setDeleteModalOpen(true)}
                                    />
                                </div>
                                <div>
                                    <V3IconClose
                                        stroke="white"
                                        onClick={() => {
                                            itemsFormik.setFieldValue("selectedItems", []);
                                        }}
                                    />
                                </div>
                            </BulkActionRightPanel>
                            <V3ConfirmationModal
                                onSubmit={() => {
                                    onDeleteHandler && onDeleteHandler(itemsFormik.values.selectedItems);
                                    setDeleteModalOpen(false);
                                    itemsFormik.setFieldValue("selectedItems", []);
                                }}
                                setModalOpen={setDeleteModalOpen}
                                modalOpen={deleteModalOpen}
                                title={"Delete item(s)"}
                                btnLabel={"Delete"}
                                useIcon
                                variant={V3ModalIconSegmentVariant.error}
                                description={"Are you sure you want to delete the selected items? This action cannot be undone."}
                            />
                        </BulkActionsPanel>
                    )}
                </>,
                bottomRightSideRefPanel.current
            );
        }

        return <></>;
    };

    const contentLength = items.length ? items[0].filteredCount : 0;

    return (
        <>
            {!hasNoResults && (
                <FadeInContainer>
                    {items.map((group, i) => (
                        <React.Fragment key={i}>
                            {group.articles.map((item) => {
                                if (!item.slug) return;

                                const itemSelected = itemsFormik.values.selectedItems.includes(item.slug);

                                return (
                                    <ContentListItemWrapper key={item.slug} $isSelectOnly={isSelectOnly} as="label" htmlFor={item.slug}>
                                        <div style={{ marginRight: isBusinessParent ? 3 : undefined }}>
                                            {!isBusinessParent && (
                                                <V3CheckBox
                                                    name={item.slug}
                                                    id={item.slug}
                                                    onChange={(e) => {
                                                        handleItemsFormikArray("selectedItems")(e);
                                                        const { checked } = e.target;
                                                        const allItemsSelected = itemsFormik.values.selectedItems.length + 1 === contentLength;

                                                        // signal the user selected all items
                                                        if (!excludeMode && checked && allItemsSelected && onFullContentSelected)
                                                            onFullContentSelected();

                                                        // signal the user deselected all items
                                                        if (excludeMode && !checked && allItemsSelected && onFullContentDeselected)
                                                            onFullContentDeselected();
                                                    }}
                                                    checked={excludeMode ? !itemSelected : itemSelected}
                                                />
                                            )}
                                        </div>
                                        <OptionalNextLink
                                            href={!isSelectOnly ? `${APPLICATION_ROUTES.CONTENT}/${item.canonicalSlug}` : undefined}
                                            customLinkStyle={{ width: "100%" }}
                                        >
                                            <ItemList
                                                item={item}
                                                isBusinessParent={isBusinessParent}
                                                isSelectOnly={isSelectOnly}
                                                collections={collections}
                                                isInfoBadgeOnly={isInfoBadgeOnly}
                                            />
                                        </OptionalNextLink>
                                    </ContentListItemWrapper>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </FadeInContainer>
            )}
            {IDLE_STATE && (
                <>
                    {!isActiveSearch ? (
                        <>{!isBusinessParent ? <V3NoContent /> : <V3BusinessParentEmpty />}</>
                    ) : (
                        <V3NoSearchResults skipExtendMobile={isSelectOnly} onClearHandler={onClearResultsHandler} />
                    )}
                </>
            )}
            {articlesLoading && (
                <VerticalSpacing top={32} bottom={32}>
                    <ContentListLoaderOffset>
                        <V3Loader justify="center" />
                    </ContentListLoaderOffset>
                </VerticalSpacing>
            )}
            <InfinitePaginationFooter
                contentLoading={articlesLoading}
                numPagesLoaded={items.length}
                contentCanFetchmore={contentCanFetchmore}
                loadMore={contentLoadmore}
                skipFooterControl={skipFooterControl}
                scrollRoot={scrollRoot}
            />
            {!isSelectOnly && <BulkActionPanelContainer />}
            {!itemsFormik.values.selectedItems.length && !isSelectOnly && !isBusinessParent && <UniversalAddContent />}
        </>
    );
};
